<!--
  서명 캔버스 — 마우스/터치/펜으로 자필 서명. v-model로 PNG data URL을 내보낸다 (빈 캔버스면 "").
  전자계약 동의 시 직원 서명용 (2026-09-12). 외부 라이브러리 없이 canvas API만 사용.
-->
<template>
  <div class="sigpad">
    <canvas
      ref="canvasRef"
      class="sigpad-canvas"
      :width="width"
      :height="height"
      @pointerdown="onDown"
      @pointermove="onMove"
      @pointerup="onUp"
      @pointerleave="onUp"
      @pointercancel="onUp"
    />
    <div v-if="!hasInk" class="sigpad-hint">여기에 마우스나 손가락으로 서명하세요</div>
    <div class="sigpad-actions">
      <el-button size="small" @click="clear">지우기</el-button>
      <span class="sigpad-name">서명자: {{ signerName }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";

const props = defineProps({
  modelValue: { type: String, default: "" },
  signerName: { type: String, default: "" },
  width: { type: Number, default: 480 },
  height: { type: Number, default: 160 },
});
const emit = defineEmits(["update:modelValue"]);

const canvasRef = ref(null);
const hasInk = ref(false);
let ctx = null;
let drawing = false;
let last = null;

function setup() {
  const c = canvasRef.value;
  ctx = c.getContext("2d");
  ctx.lineWidth = 2.5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = "#14213d";
}

function pos(e) {
  const r = canvasRef.value.getBoundingClientRect();
  // CSS 크기와 캔버스 픽셀 크기가 다를 수 있으므로 비율 보정
  return {
    x: (e.clientX - r.left) * (canvasRef.value.width / r.width),
    y: (e.clientY - r.top) * (canvasRef.value.height / r.height),
  };
}

function onDown(e) {
  e.preventDefault();
  canvasRef.value.setPointerCapture?.(e.pointerId);
  drawing = true;
  last = pos(e);
  ctx.beginPath();
  ctx.moveTo(last.x, last.y);
  ctx.lineTo(last.x + 0.1, last.y + 0.1); // 점 하나도 찍히게
  ctx.stroke();
  hasInk.value = true;
}

function onMove(e) {
  if (!drawing) return;
  e.preventDefault();
  const p = pos(e);
  ctx.beginPath();
  ctx.moveTo(last.x, last.y);
  ctx.lineTo(p.x, p.y);
  ctx.stroke();
  last = p;
}

function onUp() {
  if (!drawing) return;
  drawing = false;
  emit("update:modelValue", hasInk.value ? canvasRef.value.toDataURL("image/png") : "");
}

function clear() {
  ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
  hasInk.value = false;
  emit("update:modelValue", "");
}

watch(() => props.modelValue, (v) => { if (!v && hasInk.value) clear(); });
onMounted(setup);
</script>

<style scoped>
.sigpad { position: relative; display: inline-block; max-width: 100%; }
.sigpad-canvas {
  display: block; width: 100%; max-width: 480px; aspect-ratio: 3 / 1;
  border: 1px dashed var(--el-border-color-darker); border-radius: 4px;
  background: #fff; touch-action: none; cursor: crosshair;
}
.sigpad-hint {
  position: absolute; top: 0; left: 0; right: 0; height: 100%; max-width: 480px;
  display: flex; align-items: center; justify-content: center;
  color: var(--el-text-color-placeholder); font-size: var(--sw-font-sm); pointer-events: none;
}
.sigpad-actions { display: flex; align-items: center; gap: var(--sw-space-3); margin-top: var(--sw-space-2); }
.sigpad-name { font-size: var(--sw-font-sm); color: var(--el-text-color-secondary); }
</style>
